import { Pagination } from "@mui/material";
import { BackpackIcon, Cross2Icon } from "@radix-ui/react-icons";
import { Loader2, Package, Search, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Pbo } from "../../components/Widget/Pbo";
import { Periode } from "../../components/Widget/Periode";
import { find } from "../../services/service";
import { useEquipeStore } from "../../store/EquipeStore";
import { useStoreSurvey } from "../../store/SurveyStore";
import {
  LISTE_EQUIPE_PRESTATAIRE,
  PRESTATAIRE,
  RENDEZ_VOUS_ATTRIBUER_EQUIPE,
} from "../../Utils/constant";

export const PrestataireAttributionEquipe = () => {
  const [prestataires, setPrestataires] = useState([]);
  const equipe = useEquipeStore();
  const survey = useStoreSurvey();
  const [item, setItem] = useState(null);
  const [loadingPrestataire, setLoadingPrestataire] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);

  const [selectedEquipe, setSelectedEquipe] = useState(null);
  const [dossierPage, setDossierPage] = useState(0);
  const dossierSize = 8;

  useEffect(() => {
    const loadPrestataire = async () => {
      setLoadingPrestataire(true);
      try {
        const res = await find(PRESTATAIRE);
        const items = res?.data?.data ?? res?.data?.donnee ?? res?.data ?? [];
        setPrestataires(items);
      } catch (err) {
        console.error(err);
        setPrestataires([]);
      } finally {
        setLoadingPrestataire(false);
      }
    };
    loadPrestataire();
  }, []);

  // Gestion de la recherche avec indicateur de chargement
  const handleSearch = async (value) => {
    setSearchTerm(value);
    setIsSearching(true);
    setCurrentPage(0);
    try {
    } finally {
      setIsSearching(false);
    }
  };

  // Charger les équipes du prestataire sélectionné
  useEffect(() => {
    if (!item) {
      setSelectedEquipe(null);
      return;
    }
    setSelectedEquipe(null);
    equipe.getEquipePrestataire(LISTE_EQUIPE_PRESTATAIRE + "/" + item.id);
  }, [item]);

  // Charger les dossiers pour une équipe sélectionnée
  const loadDossiersForEquipe = (equipeItem) => {
    if (!item || !equipeItem) return;
    setSelectedEquipe(equipeItem);
    setDossierPage(0);
    survey.getData(
      `${RENDEZ_VOUS_ATTRIBUER_EQUIPE}/${item.id}?page=0&param=&size=${dossierSize}&equipeId=${equipeItem.id}`
    );
  };

  const moreDossiers = (page) => {
    if (!item || !selectedEquipe) return;
    setDossierPage(page - 1);
    survey.getData(
      `${RENDEZ_VOUS_ATTRIBUER_EQUIPE}/${item.id}?page=${
        page - 1
      }&param=&size=${dossierSize}&equipeId=${selectedEquipe.id}`
    );
  };

  return (
    <div className="mt-4">
      <div className="my-4 flex flex-col md:flex-row items-stretch md:items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <input
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            type="text"
            placeholder="Rechercher un prestataire par nom..."
            className="input input-bordered px-3 pl-10 pr-10 w-full h-11 text-sm text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
          />
          <Search className="w-4 h-4 absolute left-3 top-3.5 text-gray-400" />
          {isSearching && (
            <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-3.5 text-amber-500" />
          )}
          {searchTerm && !isSearching && (
            <button
              onClick={() => handleSearch("")}
              className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <select
          className="select select-bordered h-11 text-sm min-w-[200px] focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
          value={item?.id ?? ""}
          onChange={(e) => {
            const id = e.target.value;
            const selected = prestataires?.find(
              (p) => String(p.id) === String(id)
            );
            setItem(selected || null);
          }}
          disabled={loadingPrestataire}
        >
          <option value={""}>
            {loadingPrestataire ? "Chargement..." : "Choisir un prestataire"}
          </option>
          {prestataires?.map((item) => (
            <option key={item.id} value={item.id}>
              {item.designation}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          {!item ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="bg-amber-100 rounded-full p-6 mb-4">
                <BackpackIcon className="w-12 h-12 text-amber-600" />
              </div>
              <p className="text-sm font-medium text-gray-900 mb-1">
                Aucun prestataire sélectionné
              </p>
              <p className="text-xs text-gray-500 text-center px-4">
                Sélectionnez un prestataire pour voir ses équipes
              </p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="bg-amber-100 w-10 h-10 rounded-full flex items-center justify-center">
                    <BackpackIcon className="w-5 h-5 text-amber-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {item.designation}
                    </h3>
                    <p className="text-xs text-gray-500">
                      {equipe?.equipePrestataire?.length ?? 0} équipe(s)
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setItem(null)}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <Cross2Icon className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              </div>

              {/* Contenu - Equipes */}
              {equipe?.loading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-amber-500 mb-3" />
                  <p className="text-sm text-gray-600">
                    Chargement des équipes...
                  </p>
                </div>
              ) : equipe?.equipePrestataire?.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="bg-gray-100 rounded-full p-4 mb-3">
                    <Package className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-600 font-medium mb-1">
                    Aucune équipe trouvée
                  </p>
                  <p className="text-xs text-gray-400 text-center px-4">
                    Ce prestataire n'a pas d'équipes associées
                  </p>
                </div>
              ) : (
                <div>
                  <div className="flex flex-wrap gap-2 max-h-[280px] overflow-y-auto mb-4">
                    {equipe?.equipePrestataire?.map((t) => (
                      <div
                        key={t.id}
                        onClick={() => loadDossiersForEquipe(t)}
                        className={`border cursor-pointer border-gray-200 rounded-full ${
                          selectedEquipe?.id === t.id
                            ? "bg-main text-white border-main font-semibold"
                            : "bg-white "
                        }`}
                      >
                        <div className="flex items-center justify-between px-4 py-2 gap-3">
                          <div>
                            <p className="text-sm text-gray-900">
                              {t?.designation}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Dossiers for selected equipe */}
                  <div className="bg-gray-50 rounded-md p-4 border border-gray-100">
                    {!selectedEquipe ? (
                      <p className="text-sm text-gray-500">
                        Sélectionnez une équipe pour afficher ses dossiers
                      </p>
                    ) : survey?.loading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                      </div>
                    ) : survey?.data?.data?.length === 0 ? (
                      <p className="text-sm text-gray-500">
                        Aucun dossier pour cette équipe
                      </p>
                    ) : (
                      <>
                        <div className="grid lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-2 grid-cols-1 gap-4">
                          {survey?.data?.data?.map((d) => (
                            <div
                              key={d.id}
                              className={`relative bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow`}
                            >
                              <div className="rounded-t-xl flex items-center gap-3 border-b border-dashed border-amber-400 px-2 py-3">
                                <p className="text-[12px] font-semibold">
                                  N° {d?.abonnement?.eligibilite?.numeroTicket}
                                </p>
                              </div>
                              <div className="px-3 py-2">
                                <div className="items-center gap-2 space-y-3">
                                  <Periode item={d} />
                                  <Pbo
                                    pbo={d?.abonnement?.eligibilite?.pbo?.nom}
                                    port={d?.brin}
                                  />
                                  <div className="flex gap-1 my-3">
                                    <div>
                                      <p className="text-[13px] font-medium">
                                        {d?.abonnement?.client?.nomPrenoms}
                                      </p>
                                      <p className="text-xs flex font-semibold gap-2">
                                        {d?.abonnement.client?.numero}
                                      </p>
                                    </div>
                                  </div>
                                  <h1 className="text-xs flex gap-1 items-center my-2">
                                    Forfait :{" "}
                                    {d?.abonnement?.forfait?.designation} -{" "}
                                    {d?.abonnement?.forfait?.categorie
                                      ?.replace("_", "")
                                      .toLowerCase()}
                                  </h1>
                                  <div className="pb-3 pt-1 border-b flex gap-4 items-center">
                                    <p className="text-xs flex gap-1 items-center font-medium">
                                      {d?.abonnement?.batiment?.designation}
                                    </p>
                                    <p className="text-xs flex gap-1 font-medium">
                                      {d?.abonnement?.equipement?.designation}
                                    </p>
                                  </div>
                                </div>
                                <h1 className="my-2 text-[13px] font-medium flex items-center">
                                  <BackpackIcon />
                                  {d?.abonnement?.commune +
                                    ", " +
                                    d?.abonnement?.quartier}
                                </h1>
                                <div className="flex items-center gap-1 mt-2">
                                  <h5 className="text-xs font-medium">
                                    Etape:
                                  </h5>
                                  <p className="bg-blue-50 text-blue-900 py-1 px-1 rounded-lg font-medium text-xs truncate">
                                    {d?.abonnement?.status
                                      ?.toLowerCase()
                                      .replace(/_/g, " ")}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="my-3 flex justify-end">
                          <Pagination
                            onChange={(event, newValue) =>
                              moreDossiers(newValue)
                            }
                            count={survey?.data?.totalPages}
                            variant="outlined"
                            color="primary"
                            shape="rounded"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
